// What a Storyteller can do to one seat: assign a character, toggle Poisoned and Drunk, and mark
// the player dead or alive (and whether their role is revealed). Reseating and removing live under
// the seat on the board, where you can see the circle.

import { useState } from "react";
import {
  Beer, Eye, EyeOff, FlaskConical, Frown, Heart, Shield, Skull, Smile, Ticket, UserRound, X,
} from "lucide-react";
import { BASE_CHARACTERS, TEAM_ORDER } from "@cc/shared";
import type { Ack, GameView, ScriptCharacter } from "@cc/shared";
import { useGame } from "../../state/game.js";
import { characterName, cn, findCharacter, teamColor, teamLabel } from "../../lib/ui.js";
import { Button, Dialog, DialogContent, Label, Tooltip } from "../ui/primitives.js";

/** A toggle whose lit state means "this is true right now". */
function Toggle({
  on,
  label,
  icon,
  tip,
  onClick,
  disabled = false,
}: {
  on: boolean;
  label: string;
  icon: React.ReactNode;
  tip: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Tooltip content={tip} disabled={disabled}>
      <Button
        variant={on ? "primary" : "secondary"}
        aria-pressed={on}
        disabled={disabled}
        onClick={onClick}
      >
        {icon}
        {label}
      </Button>
    </Tooltip>
  );
}

/** One labelled group on the status/life row. */
function Group({
  title,
  divider = false,
  children,
}: {
  title: string;
  /** Draws the hairline that separates this group from the one to its left. */
  divider?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("min-w-0", divider && "border-l border-ink-line/60 pl-3")}>
      <Label>{title}</Label>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

/** One row of character buttons under a heading. */
function CharacterGroup({
  heading,
  color,
  characters,
  selectedId,
  onPick,
}: {
  heading: string;
  color?: string;
  characters: readonly Pick<ScriptCharacter, "id" | "name" | "ability" | "icon">[];
  selectedId: string | null;
  onPick: (id: string) => void;
}) {
  if (characters.length === 0) return null;
  return (
    <div className="mb-2">
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide" style={{ color }}>
        {heading}
      </h4>
      <div className="flex flex-wrap gap-1">
        {characters.map((c) => (
          <Tooltip key={c.id} content={c.ability ?? c.name}>
            <button
              type="button"
              onClick={() => onPick(c.id)}
              aria-pressed={selectedId === c.id}
              className={cn(
                "flex items-center gap-1 rounded border px-1.5 py-1 text-xs transition",
                selectedId === c.id
                  ? "border-brass bg-brass/20 text-parchment"
                  : "border-ink-line text-parchment-dim hover:text-parchment",
              )}
            >
              {c.icon && <img src={c.icon} alt="" className="size-6 object-contain" />}
              {c.name}
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}

export function SeatDialog({
  view,
  seatId,
  onClose,
}: {
  view: GameView;
  seatId: string | null;
  onClose: () => void;
}) {
  const send = useGame((s) => s.send);
  const clearError = useGame((s) => s.clearError);
  // A refusal is shown here rather than on the page behind the overlay.
  const [error, setError] = useState<string | null>(null);

  const seat = view.seats.find((s) => s.id === seatId) ?? null;
  const character = findCharacter(view, seat?.characterId);

  if (!seat) return null;

  const scriptTravelers = view.script.characters.filter((c) => c.team === "traveler" && !c.offScript);
  const listed = new Set(scriptTravelers.map((c) => c.id));
  const otherTravelers = BASE_CHARACTERS.filter((c) => c.team === "traveler" && !listed.has(c.id));
  const alignmentTip = seat.isTraveler
    ? "This Traveler's side, needed before Reveal Roles. Only they and you see it."
    : "Overrides their side independent of character. They will see this too.";

  const report = (ack: Ack): void => {
    setError(ack.ok ? null : ack.error);
    if (!ack.ok) clearError();
  };
  const set = (payload: Record<string, unknown>): void => {
    void send("st:setSeatStatus", { seatId: seat.id, ...payload } as never).then(report);
  };
  const assign = (characterId: string | null): void => {
    void send("st:assignRole", { seatId: seat.id, characterId }).then(report);
  };
  const close = (): void => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={seatId !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent
        title={
          <>
            {seat.displayName}
            <span className="text-xs font-normal text-parchment-dim">
              {character
                ? `${character.name} — ${teamLabel(character.team, seat.alignmentOverride)}${seat.secretCharacterId ? ` (secretly the ${characterName(view, seat.secretCharacterId) ?? seat.secretCharacterId})` : ""}`
                : "No character assigned"}
            </span>
          </>
        }
        description={
          seat.alive
            ? undefined
            : seat.deathCause === "executed"
              ? "Executed"
              : seat.deathCause === "exiled"
                ? "Exiled"
                : "Dead at night"
        }
        // Wide enough that Status and Life stay on one row even for a dead seat, where Life grows
        // from two buttons to three.
        className="w-[min(48rem,calc(100vw-2rem))]"
        // Focus the dialog itself: Radix would focus the first control, whose tooltip then covers
        // the dialog's title.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        <div className="space-y-4">
          {error && (
            <div role="alert" className="flex items-start gap-2 rounded border border-evil/70 bg-ink-soft px-3 py-2 text-xs">
              <span className="flex-1 text-parchment">{error}</span>
              <button type="button" onClick={() => setError(null)} className="text-parchment-dim hover:text-parchment">
                <X className="size-4" />
              </button>
            </div>
          )}

          <div className="flex flex-wrap items-start gap-3">
            {/* --- who they are right now --- */}
            <Group title="Status">
              <Toggle
                on={seat.poisoned ?? false}
                label="Poisoned"
                icon={<FlaskConical className="size-4" />}
                tip="Their ability malfunctions tonight. Only you ever see this."
                onClick={() => set({ poisoned: !seat.poisoned })}
              />
              <Toggle
                on={seat.drunk ?? false}
                label="Drunk"
                icon={<Beer className="size-4" />}
                tip="They believe they are their character but their ability does nothing. Only you ever see this."
                onClick={() => set({ drunk: !seat.drunk })}
              />
              <Toggle
                on={seat.protected ?? false}
                label="Protected"
                icon={<Shield className="size-4" />}
                tip="Safe from the Demon's kill tonight only. Only you ever see this — clears itself when the night ends."
                onClick={() => set({ protected: !seat.protected })}
              />
              <Toggle
                on={seat.isTraveler}
                label="Traveler"
                icon={<UserRound className="size-4" />}
                tip="Sits in the circle and votes. Randomize Roles skips them; give them any Traveler and an alignment by hand."
                onClick={() => set({ isTraveler: !seat.isTraveler })}
              />
            </Group>

            {/* --- their side: always set for a Traveler, otherwise only if something flipped it --- */}
            <Group title="Alignment" divider>
              <Toggle
                on={seat.alignmentOverride === "good"}
                label="Good"
                icon={<Smile className="size-4" />}
                tip={alignmentTip}
                onClick={() => set({ alignmentOverride: seat.alignmentOverride === "good" ? null : "good" })}
              />
              <Toggle
                on={seat.alignmentOverride === "evil"}
                label="Evil"
                icon={<Frown className="size-4" />}
                tip={alignmentTip}
                onClick={() => set({ alignmentOverride: seat.alignmentOverride === "evil" ? null : "evil" })}
              />
            </Group>

            {/* --- what has happened to them --- */}
            <Group title="Life" divider>
              {seat.alive ? (
                <>
                  <Tooltip content="Kills them by today's execution. Ends the day.">
                    <Button
                      variant="danger"
                      onClick={() => set({ alive: false, deathCause: "executed" })}
                    >
                      <Skull className="size-4" /> Executed
                    </Button>
                  </Tooltip>
                  <Tooltip content="Kills them without an execution — the Demon, a Mayor bounce, anything else.">
                    <Button variant="danger" onClick={() => set({ alive: false, deathCause: "night" })}>
                      <Skull className="size-4" /> Killed at night
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip content="Brings them back to life. Their ghost vote goes with it.">
                    <Button onClick={() => set({ alive: true })}>
                      <Heart className="size-4" /> Revive
                    </Button>
                  </Tooltip>
                  {!seat.isTraveler && (
                    <Toggle
                      on={seat.characterIsPublic}
                      label={seat.characterIsPublic ? "Role is public" : "Reveal role"}
                      icon={seat.characterIsPublic ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
                      tip="Shows this seat's character to the whole table, as when a Storyteller turns a dead player's token face up."
                      disabled={!seat.characterId}
                      onClick={() => set({ revealCharacter: !seat.characterIsPublic })}
                    />
                  )}
                  <Toggle
                    on={!seat.ghostVoteUsed}
                    label={seat.ghostVoteUsed ? "Ghost vote spent" : "Ghost vote left"}
                    icon={<Ticket className="size-4" />}
                    tip="A dead player has one vote for the whole game. Toggle this if it needs correcting by hand."
                    onClick={() => set({ ghostVoteUsed: !seat.ghostVoteUsed })}
                  />
                </>
              )}
            </Group>
          </div>

          {/* --- their character: the whole script, grouped by team --- */}
          <section>
            <div className="mb-1.5 flex items-center gap-2">
              <Label>Character</Label>
              {seat.characterId && (
                <Button size="sm" variant="ghost" className="ml-auto" onClick={() => assign(null)}>
                  Clear character
                </Button>
              )}
            </div>

            {seat.isTraveler ? (
              <div className="max-h-[18rem] overflow-y-auto cc-scroll">
                <CharacterGroup
                  heading="Travelers on this script"
                  color={teamColor("traveler")}
                  characters={scriptTravelers}
                  selectedId={seat.characterId}
                  onPick={assign}
                />
                <CharacterGroup
                  heading={scriptTravelers.length > 0 ? "Other Travelers" : "Travelers"}
                  color={teamColor("traveler")}
                  characters={otherTravelers}
                  selectedId={seat.characterId}
                  onPick={assign}
                />
              </div>
            ) : view.script.characters.length === 0 ? (
              <p className="text-sm text-parchment-dim">Import a script first.</p>
            ) : (
              <div className="max-h-[18rem] overflow-y-auto cc-scroll">
                {TEAM_ORDER.filter((team) => team !== "traveler").map((team) => (
                  <CharacterGroup
                    key={team}
                    heading={teamLabel(team)}
                    color={teamColor(team)}
                    characters={view.script.characters.filter((c) => c.team === team)}
                    selectedId={seat.characterId}
                    onPick={assign}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
